import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import NextGenPlayer from "../../video-player/Components/NextGenPlayer";
import { createAvodPlugin } from "../../video-player/Plugins/builtins/avodPlugin";
import {
  fetchTimeline,
  trackBroadcastEvent,
  toggleFavoriteChannel,
  getFavoriteChannelIds,
  pushRecentChannel,
} from "./channelScheduleApi";
import { controlPlaneFromChannelNow } from "./controlPlaneFromChannelNow";
import {
  resolveChannelStreamUrl,
  safeCssBackgroundUrl,
  collectChannelStreamCandidates,
} from "./channelPlaybackHelpers";
import { useChannelNow } from "./useChannelNow";
import "./BroadcastStudio.css";
import "./ChannelTunePage.css";

/**
 * Tune a channel end-to-end through the Media Asset control plane.
 * Player does not know the asset came from the Program Scheduler.
 */
function ChannelTunePage(props) {
  const channelKey =
    (props.match && props.match.params && props.match.params.channelId) || "";
  const showSchedule =
    props.match && props.match.path && props.match.path.indexOf("/schedule") >= 0;

  const [schedule, setSchedule] = useState([]);
  const [playerError, setPlayerError] = useState(null);
  const [failoverIndex, setFailoverIndex] = useState(0);
  const [favorites, setFavorites] = useState(() => getFavoriteChannelIds());
  const [tunedOnce, setTunedOnce] = useState(false);

  const {
    nowPayload,
    error,
    loading,
    refresh: refreshNow,
  } = useChannelNow(channelKey);

  useEffect(() => {
    setPlayerError(null);
    setFailoverIndex(0);
    setSchedule([]);
    setTunedOnce(false);
  }, [channelKey]);

  useEffect(() => {
    if (!nowPayload || nowPayload.success === false || tunedOnce) return;
    const ch = nowPayload.channel || {};
    pushRecentChannel(ch.id || ch.unique_id || channelKey);
    trackBroadcastEvent({
      event_type: "tune",
      channel_id: ch.id,
      organization_id: null,
      media_asset_key: nowPayload.media_asset && nowPayload.media_asset.id,
      schedule_entry_uid: nowPayload.current && nowPayload.current.unique_id,
    });
    setTunedOnce(true);
  }, [nowPayload, channelKey, tunedOnce]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showSchedule || !nowPayload || !nowPayload.channel || !nowPayload.channel.id) {
        return;
      }
      const from = new Date();
      from.setHours(0, 0, 0, 0);
      const to = new Date(from.getTime() + 86400000);
      try {
        const tl = await fetchTimeline({
          channel_id: nowPayload.channel.id,
          from: from.toISOString(),
          to: to.toISOString(),
        });
        if (!cancelled) setSchedule((tl && tl.entries) || []);
      } catch (e) {
        if (!cancelled) setSchedule([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nowPayload, showSchedule]);

  const tuned = useMemo(() => {
    if (!nowPayload || nowPayload.success === false) return null;
    return controlPlaneFromChannelNow(nowPayload, {});
  }, [nowPayload]);

  const streamUrl = useMemo(
    () => resolveChannelStreamUrl(tuned, null, failoverIndex),
    [tuned, failoverIndex]
  );

  const channel = (nowPayload && nowPayload.channel) || {};
  const isFav = favorites.indexOf(String(channel.id || channel.unique_id)) >= 0;
  const posterUrl =
    (nowPayload &&
      nowPayload.slate &&
      nowPayload.slate.image &&
      nowPayload.slate.image.url) ||
    channel.banner ||
    "";

  const plugins = useMemo(() => {
    if (!tuned || !tuned.avodMode || !tuned.avodMode.enableAvod) return [];
    try {
      return [createAvodPlugin({ enabled: true })];
    } catch (e) {
      return [];
    }
  }, [tuned]);

  const playerConfig = useMemo(() => {
    if (!streamUrl || !tuned) return null;
    const asset = tuned.plane.mediaAsset || {};
    const startTime =
      typeof tuned.joinStartTime === "number" && tuned.joinStartTime > 0
        ? tuned.joinStartTime
        : 0;
    return {
      source: streamUrl,
      contentId: String(asset.id || channelKey),
      title: asset.title || channel.name,
      poster: posterUrl || undefined,
      features: tuned.plane.features,
      autoplay: true,
      mediaAsset: asset,
      // Continuous join-NOW only (0 for non-continuous / just-started).
      startTime: startTime,
    };
  }, [streamUrl, tuned, channelKey, channel.name, posterUrl]);

  useEffect(() => {
    setPlayerError(null);
    setFailoverIndex(0);
  }, [streamUrl]);

  const handlePlayerError = (err) => {
    const candidates = collectChannelStreamCandidates(tuned, null);
    if (failoverIndex + 1 < candidates.length) {
      setFailoverIndex(failoverIndex + 1);
      setPlayerError(null);
      return;
    }
    setPlayerError(err);
    refreshNow({ silent: true });
  };

  const handleEnded = () => {
    setPlayerError(null);
    refreshNow({ silent: true });
  };

  const handlePlaybackEvent = (name) => {
    var type = name;
    if (type === "PLAYBACK_STARTED" || type === "playing" || type === "PLAYING") {
      setPlayerError(null);
    }
  };

  return (
    <div className="bcast-studio channel-tune">
      <Helmet>
        <title>
          {(channel.name || "Channel") + " · Watch Live"}
        </title>
      </Helmet>

      <header className="bcast-studio__header">
        <div>
          <p className="bcast-studio__eyebrow">Live Channel</p>
          <h1>{channel.name || "Channel"}</h1>
        </div>
        <nav className="bcast-studio__nav">
          <Link to="/guide">Guide</Link>
          <Link to="/channels">Channels</Link>
          <Link to={"/channel/" + channelKey + "/schedule"}>Full schedule</Link>
        </nav>
      </header>

      {loading && <p className="bcast-studio__status">Tuning…</p>}
      {error && <p className="bcast-studio__error">{error}</p>}

      {tuned && (
        <div className="channel-tune__layout">
          <div className="channel-tune__player">
            {streamUrl && playerConfig ? (
              <NextGenPlayer
                key={(tuned.plane.mediaAsset.id || channelKey) + ":" + streamUrl}
                config={Object.assign({}, playerConfig, { plugins: plugins })}
                onError={handlePlayerError}
                onEnded={handleEnded}
                onPlaybackEvent={handlePlaybackEvent}
              />
            ) : (
              <div
                className="channel-tune__slate"
                style={{
                  backgroundImage: safeCssBackgroundUrl(
                    (nowPayload.slate &&
                      nowPayload.slate.image &&
                      nowPayload.slate.image.url) ||
                      channel.banner
                  ),
                }}
              >
                <div className="channel-tune__slate-copy">
                  <p className="epg-live">{(nowPayload.phase || "slate").toUpperCase()}</p>
                  <h2>
                    {(nowPayload.current && nowPayload.current.title) ||
                      tuned.plane.mediaAsset.title ||
                      "Standing by"}
                  </h2>
                  <p>
                    Media Asset resolved via Program Scheduler · player awaits stream URL on
                    the asset primary resource.
                  </p>
                  <button type="button" className="bcast-btn" onClick={() => refreshNow()}>
                    Retry now-playing
                  </button>
                </div>
              </div>
            )}
            {playerError ? (
              <p className="bcast-studio__error">
                {(playerError && (playerError.message || playerError.code)) ||
                  "Playback error"}
              </p>
            ) : null}
          </div>

          <aside className="channel-tune__meta">
            <p className="epg-label">Now</p>
            <h2>{(nowPayload.current && nowPayload.current.title) || "Off air"}</h2>
            <p className="epg-label">Up Next</p>
            <h3>{(nowPayload.next && nowPayload.next.title) || "—"}</h3>

            <dl className="ops-dl">
              <div>
                <dt>Phase</dt>
                <dd>{nowPayload.phase}</dd>
              </div>
              <div>
                <dt>Strategy</dt>
                <dd>{tuned.plane.strategy && tuned.plane.strategy.id}</dd>
              </div>
              <div>
                <dt>Capabilities</dt>
                <dd>{(tuned.plane.enabledCapabilities || []).join(", ")}</dd>
              </div>
              <div>
                <dt>Advertising</dt>
                <dd>
                  {(tuned.normalized.advertising && tuned.normalized.advertising.policy) || "—"}
                </dd>
              </div>
            </dl>

            <div className="ops-actions">
              <button
                type="button"
                className="bcast-btn"
                onClick={() => {
                  const next = toggleFavoriteChannel(channel.id || channel.unique_id);
                  setFavorites(next);
                }}
              >
                {isFav ? "Unfavorite" : "Favorite"}
              </button>
              <button
                type="button"
                className="bcast-btn bcast-btn--ghost"
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) {
                    navigator.share({ title: channel.name, url: url });
                  } else if (navigator.clipboard) {
                    navigator.clipboard.writeText(url);
                  }
                  trackBroadcastEvent({
                    event_type: "share",
                    channel_id: channel.id,
                  });
                }}
              >
                Share channel
              </button>
              <Link className="bcast-btn bcast-btn--ghost" to="/guide">
                What&apos;s on now
              </Link>
            </div>
          </aside>
        </div>
      )}

      {showSchedule && (
        <section className="channel-tune__schedule">
          <h2>Today&apos;s schedule</h2>
          <ul>
            {schedule.map((e) => (
              <li key={e.id}>
                <span>
                  {e.starts_at
                    ? new Date(e.starts_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : ""}
                </span>
                <strong>{e.title || e.entry_kind}</strong>
              </li>
            ))}
            {!schedule.length && <li>No programs scheduled for today.</li>}
          </ul>
        </section>
      )}
    </div>
  );
}

export default ChannelTunePage;
